import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import { Link, useNavigate } from "react-router-dom";

import TenantForm from "components/TenantForm/TenantForm";
import { PrimaryButton } from "widgets";
import {
  useCreateTenant,
  useFetchCountryList,
  useFetchGradeList,
  useFetchVerticalList,
} from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";
import routes from "constants/routes";
import {
  buildTenantPayload,
  TENANT_FORM_INITIAL_VALUES,
  createTenantSchema,
  getCountryOptions,
  mapGradesToOptions,
  mapVerticalsToOptions,
} from "helpers/tenantUtils";
import { SUCCESS_REDIRECT_SNACKBAR_DELAY_MS } from "constants/constants";

import styles from "./CreateTenant.module.scss";

const handleCreateSubmit = (
  values,
  countryOptions,
  createTenant,
  openSnackbar,
  navigate,
  setSubmitting
) => {
  const payload = buildTenantPayload(values, countryOptions);
  createTenant(payload, {
    onSuccess: (response) => {
      setSubmitting(false);
      const tenantId = response.data.tenantId;
      navigate(`/tenantDetails?tenantId=${tenantId}`);
      setTimeout(() => {
        openSnackbar("Tenant created successfully!", "success");
      }, SUCCESS_REDIRECT_SNACKBAR_DELAY_MS);
    },
    onError: (error) => {
      setSubmitting(false);
      openSnackbar("Error creating tenant: " + error.message, "error");
    },
  });
};

const CreateTenant = () => {
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const { mutate: createTenant, isPending: isCreateTenantLoading } = useCreateTenant();
  const {
    data: verticalList,
    isPending: isVerticalListLoading,
    isError: isErrorOnVerticalList,
    error: errorMessageOnFetchVerticalList,
  } = useFetchVerticalList();

  const {
    data: gradeList,
    isPending: isGradeListLoading,
    isError: isErrorOnGradeList,
    error: errorMessageOnFetchGradeList,
  } = useFetchGradeList();

  const {
    data: countryList,
    isPending: isCountryListLoading,
    isError: isErrorOnCountryList,
    error: errorMessageOnFetchCountryList,
  } = useFetchCountryList();

  const verticalOptions = verticalList?.items
    ? mapVerticalsToOptions(verticalList.items)
    : [];
  const gradeOptions = gradeList?.items
    ? mapGradesToOptions(gradeList.items)
    : [];
  const countryOptions = useMemo(
    () => (countryList?.items ? getCountryOptions(countryList.items) : []),
    [countryList],
  );
  const tenantSchema = useMemo(
    () => createTenantSchema(countryOptions),
    [countryOptions],
  );

  return (
    <div className={styles.mainContainer}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Link className={styles.backLink} to={routes.tenants.tenants}>
            ← Back to tenants
          </Link>
          <h1 className={styles.pageTitle}>Create tenant</h1>
        </div>
      </div>

      <div className={styles.formWrap}>
        <Formik
          initialValues={TENANT_FORM_INITIAL_VALUES}
          validationSchema={tenantSchema}
          validateOnMount={false}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            handleCreateSubmit(
              values,
              countryOptions,
              createTenant,
              openSnackbar,
              navigate,
              setSubmitting
            );
          }}
        >
          {({
            values,
            errors,
            touched,
            dirty,
            setFieldValue,
            setFieldTouched,
            handleBlur,
            handleSubmit,
            isSubmitting,
            submitCount,
          }) => (
            <Form onSubmit={handleSubmit}>
              <TenantForm
                values={values}
                touched={touched}
                dirty={dirty}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleBlur={handleBlur}
                errors={errors}
                submitCount={submitCount}
                verticalOptions={verticalOptions}
                gradeOptions={gradeOptions}
                countryOptions={countryOptions}
                isVerticalOrGradeListLoading={isVerticalListLoading || isGradeListLoading}
                isCountryListLoading={isCountryListLoading}
                isErrorOnVerticalList={isErrorOnVerticalList}
                isErrorOnGradeList={isErrorOnGradeList}
                isErrorOnCountryList={isErrorOnCountryList}
                errorMessageOnFetchVerticalList={errorMessageOnFetchVerticalList}
                errorMessageOnFetchGradeList={errorMessageOnFetchGradeList}
                errorMessageOnFetchCountryList={errorMessageOnFetchCountryList}
              />
              <div className={styles.submitRow}>
                <PrimaryButton type="submit" disabled={isSubmitting || isCreateTenantLoading}>
                  {isCreateTenantLoading || isSubmitting ? <Loader /> : "Create tenant"}
                </PrimaryButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateTenant;
